import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Play, GraduationCap, UtensilsCrossed, Briefcase, ShoppingBag, Plane, Users } from "lucide-react";
import { Logo } from "../landing/Logo";
import { scrollToId } from "../landing/Navbar";

const LINES = [
  { text: "Learn.", cls: "text-white" },
  { text: "Earn.", cls: "bg-gradient-to-r from-[#635BFF] to-[#00D26A] bg-clip-text text-transparent" },
  { text: "Build.", cls: "text-white" },
  { text: "Belong.", cls: "bg-gradient-to-r from-[#00D26A] to-[#FF7A00] bg-clip-text text-transparent" },
];

const AUDIENCE = ["Students", "Creators", "Chefs", "Recruiters", "Farmers", "Entrepreneurs"];

const STATS = [
  { v: "25K+", l: "Students" },
  { v: "250+", l: "Recruiters" },
  { v: "100+", l: "Communities" },
];

const NOVA_NODES = [
  { icon: GraduationCap, label: "LearnNova", cls: "left-1/2 top-0 -translate-x-1/2", delay: 0 },
  { icon: UtensilsCrossed, label: "FoodNova", cls: "left-0 top-[30%]", delay: 0.6 },
  { icon: Briefcase, label: "CareerNova", cls: "right-0 top-[30%]", delay: 1.2 },
  { icon: ShoppingBag, label: "ShopNova", cls: "left-[6%] bottom-[8%]", delay: 1.8 },
  { icon: Plane, label: "TravelNova", cls: "right-[6%] bottom-[8%]", delay: 2.4 },
  { icon: Users, label: "CommunityNova", cls: "left-1/2 bottom-0 -translate-x-1/2", delay: 3 },
];

export const SiteHero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -160]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="site-hero"
      className="relative overflow-hidden px-4 pb-28 pt-40 text-center sm:pt-44"
    >
      <motion.div style={{ y: y1 }} aria-hidden="true" className="absolute -left-48 top-16 h-[520px] w-[520px] rounded-full bg-[#635BFF]/15 blur-[150px]" />
      <motion.div style={{ y: y2 }} aria-hidden="true" className="absolute -right-40 top-72 h-[460px] w-[460px] rounded-full bg-[#00D26A]/10 blur-[150px]" />
      <div aria-hidden="true" className="absolute left-1/2 top-1/3 h-[380px] w-[680px] -translate-x-1/2 rounded-full bg-[#FF7A00]/[0.05] blur-[160px]" />

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.15 }}
        className="relative z-10 mb-8 inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-white/5 px-4 py-2 backdrop-blur-md"
      >
        <span className="animate-pulse-dot h-2 w-2 rounded-full bg-[#00D26A]" />
        <span className="font-jbmono text-[11px] uppercase tracking-[0.25em] text-slate-300">
          The Gen Z Economic Operating System
        </span>
      </motion.div>

      <h1
        data-testid="site-hero-heading"
        className="relative z-10 font-display text-[15vw] font-black leading-[0.94] tracking-tight sm:text-8xl"
      >
        {LINES.map((line, i) => (
          <span key={line.text} className="block overflow-hidden pb-1">
            <motion.span
              className={`block ${line.cls}`}
              initial={{ y: "112%" }}
              animate={{ y: 0 }}
              transition={{ duration: 0.95, delay: 0.35 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
            >
              {line.text}
            </motion.span>
          </span>
        ))}
      </h1>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.05 }}
        className="relative z-10 mt-8 flex flex-wrap items-center justify-center gap-2"
      >
        <span className="mr-1 font-jbmono text-[11px] uppercase tracking-[0.25em] text-slate-500">
          One platform for
        </span>
        {AUDIENCE.map((a) => (
          <span key={a} className="rounded-full border border-white/10 bg-white/[0.04] px-3.5 py-1.5 text-xs font-medium text-slate-300 backdrop-blur-md">
            {a}
          </span>
        ))}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.2 }}
        className="relative z-10 mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row"
      >
        <button
          data-testid="hero-cta-start"
          onClick={scrollToId("#claim")}
          className="glow-btn-z rounded-full bg-gradient-to-r from-[#635BFF] to-[#00D26A] px-9 py-4 font-display text-sm font-bold uppercase tracking-wider text-white"
        >
          Get Started
        </button>
        <button
          data-testid="hero-cta-demo"
          onClick={scrollToId("#download")}
          className="group flex items-center gap-2.5 rounded-full border border-white/15 bg-white/5 px-9 py-4 font-display text-sm font-bold uppercase tracking-wider text-slate-200 backdrop-blur-md transition-colors hover:border-[#635BFF]/60 hover:text-white"
        >
          <Play className="h-4 w-4 fill-current" />
          Watch Demo
        </button>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.45 }}
        className="relative z-10 mt-14 flex items-center justify-center gap-10 sm:gap-16"
        data-testid="hero-stats"
      >
        {STATS.map((s) => (
          <div key={s.l}>
            <p className="font-display text-3xl font-black text-white sm:text-4xl">{s.v}</p>
            <p className="mt-1 font-jbmono text-[10px] uppercase tracking-[0.25em] text-slate-500">{s.l}</p>
          </div>
        ))}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 1.6 }}
        className="relative z-10 mx-auto mt-20 h-[380px] w-full max-w-[520px] sm:h-[420px]"
        data-testid="hero-ecosystem-visual"
      >
        <div aria-hidden="true" className="absolute left-1/2 top-1/2 h-[74%] w-[74%] -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-white/10" />
        <div aria-hidden="true" className="absolute left-1/2 top-1/2 h-[46%] w-[46%] -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/5" />
        <div className="absolute left-1/2 top-1/2 z-10 flex h-24 w-24 -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-3xl border border-[#635BFF]/40 bg-[#111827] shadow-[0_0_80px_-10px_rgba(99,91,255,0.5)]">
          <Logo className="h-10 w-10" />
        </div>
        {NOVA_NODES.map((n) => (
          <motion.div
            key={n.label}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.9 + n.delay * 0.2, duration: 0.5 }}
            className={`absolute ${n.cls}`}
          >
            <div
              className="animate-floaty flex items-center gap-2 rounded-2xl border border-white/10 bg-[#111827]/90 px-4 py-2.5 backdrop-blur-xl"
              style={{ animationDelay: `${n.delay * 0.7}s` }}
            >
              <n.icon className="h-4 w-4 text-[#00D26A]" />
              <span className="font-display text-xs font-bold text-white sm:text-sm">{n.label}</span>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
};
